import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './Colors.css';
import { KeyListener } from 'react-game-kit';
import { Stage } from 'react-game-kit';
import Scoreboard from './Scoreboard';
import GameArea from './GameArea';
import CompletionMeter from './CompletionMeter';
import { FrenzyKeys, LEVEL_COUNTER_INITIAL_VALUE, SCREEN_WIDTH, SCREEN_HEIGHT, DEFAULT_DISPLAY_PIXEL_MULTIPLIER } from './FrenzyConstants';
import { createLevel, Level, DrawMode } from './Model';
import { minMax } from './Utils';
import DebounceableComponent from './utils/debounce';

type Props = {
	onGameOver: (finalScore: number) => void;
	onGameExit: () => void;
}

type State = {
	pauseRequested: boolean,
	paused: boolean,
	unpauseRequested: boolean,
	scale: number;
	scaleChangeRequested: boolean;
	drawRequested: boolean,
	drawMode: DrawMode,
	level: Level,
	lives: number;
	score: number;
	counterValue: number,
	counterEnabled: boolean,
	completionPercentage: number,
	performBonusCountdown: boolean,
	currentBonusDisplayed: number,
	counterValueAtBonusCountdown: number
};

class FrenzyMain extends DebounceableComponent<Props, State> {
	private loopSubscriptionId: number;
	private lastUpdatedCounter: number;

  static contextTypes = {
    loop: PropTypes.object,
	};
	
	static getLevelStartState = () => {
		return {
			drawRequested: false,
			drawMode: DrawMode.NOT_DRAWING,
			counterValue: LEVEL_COUNTER_INITIAL_VALUE,
			counterEnabled: true,
			completionPercentage: 100,
			performBonusCountdown: false,
			counterValueAtBonusCountdown: 0
		}
	}

	constructor(props: Props) {
		super(props);
		this.loopSubscriptionId = 0;
		this.lastUpdatedCounter = 0;
		const level = createLevel(1);
		this.state = {
			...FrenzyMain.getLevelStartState(),
			pauseRequested: false,
			paused: false,
			unpauseRequested: false,
			scale: DEFAULT_DISPLAY_PIXEL_MULTIPLIER,
			scaleChangeRequested: false,
			level,
			lives: 2,
			score: 0,
			currentBonusDisplayed: level.bonusMultiplier,
		}
		this.lastUpdatedCounter = new Date().getTime();
	}

  componentDidMount() {
    this.loopSubscriptionId = this.context.loop.subscribe(this.update);
    this.keyListener.subscribe([ 
			FrenzyKeys.P, 
			FrenzyKeys.Q,
			FrenzyKeys.S,
			this.keyListener.SPACE
		]);
  }

  componentWillUnmount() {
    this.context.loop.unsubscribe(this.loopSubscriptionId);
		this.keyListener.unsubscribe();
	}


	
	handleKeysWhilePaused = () => {
		if (this.debounce(
			this.state.pauseRequested, 
			FrenzyKeys.P, 
		{
			pauseRequested: false,
			paused: true
		},
		"Looking for P while paused")) {
			return;
		}

		if (this.debounce(this.state.unpauseRequested, FrenzyKeys.P, {
				unpauseRequested: false,
				paused: false
			},
			"Looking for P while unpause requested")) {
			return;
		}

		if (this.debounce(this.state.scaleChangeRequested, FrenzyKeys.S, {
				scaleChangeRequested: false,
				scale: this.state.scale === 4 ? 1 : (this.state.scale + 1)
			})) {
			return;
		}

		if (this.keyListener.isDown(FrenzyKeys.Q)) {
			this.props.onGameExit();
		}
		if (this.keyListener.isDown(FrenzyKeys.P)) {
			this.setState({unpauseRequested: true});
		}
		if (this.keyListener.isDown(FrenzyKeys.S)) {
			this.setState({scaleChangeRequested: true});
		}
	}

	handleGameplayKeys = () => {
		if (this.keyListener.isDown(FrenzyKeys.P)) {
			this.setState({pauseRequested: true});
		}

		if (this.debounce(this.state.drawRequested, this.keyListener.SPACE, {
			drawRequested: false,
			drawMode: Math.min(2, this.state.drawMode + 1)
		},
		"In FM:drawRequested, looking for space"
		)) {
			return;
		}

		if (this.keyListener.isDown(this.keyListener.SPACE)) {
			this.setState({drawRequested: true});
		}
	}

	onPlayerDied = () => {
		const { lives, score } = this.state;

		const newLives = lives - 1;
		if (newLives >= 0) {
			this.setState({
				drawMode: DrawMode.NOT_DRAWING,
				lives: newLives
			});
		}
		return (newLives < 0);
	}
	onDrawFinished = () => {
		this.setState({drawMode: DrawMode.NOT_DRAWING});
	}

	onCompletionPercentageChanged = (completionPercentage:number):boolean => {
		if (completionPercentage < 25) {
			console.log(`comp perc changed to ${completionPercentage}`)
		}
		this.setState({completionPercentage});
		if (completionPercentage < 5) {
			console.log(`comp perc changed to ${completionPercentage} - transitioning`)
			return true;
		}
		return false;
	}

	onLeptonsKilled = (numberOfLeptonsKilled: number) => {
		const { currentBonusDisplayed } = this.state;
		const newTotal = currentBonusDisplayed + numberOfLeptonsKilled;
		console.log(`Leptons killed; ${numberOfLeptonsKilled} - adding to existing ${currentBonusDisplayed} to get ${newTotal}`)
		
		this.setState({
			currentBonusDisplayed: newTotal
		});
	}

	onFinalLeptonKilled = () => {
		// not doing anything - can remove?
	}


	onScoreIncrement = (pointsToIncrement: number) => {
		this.setState({
			score: this.state.score + pointsToIncrement,
		});
}

	onLevelTransitionFinished = (finalPointsIncrement: number) => {
			console.log(`Drain complete. Triggering bonus countdown`)
			this.setState({
				score: this.state.score + finalPointsIncrement,
				performBonusCountdown: true,
				counterValueAtBonusCountdown: this.state.counterValue
			});
	}

	onFinalScoreCalculated = (finalPointsIncrement: number) => {
			console.log(`Final tally complete`)
			const finalScore = this.state.score + finalPointsIncrement
			this.setState({
				score: finalScore,
			});
			this.props.onGameOver(finalScore);
	}

	onBonusCountdownComplete = () => {
		const newLevelNumber = this.state.level.number + 1;
		let { lives } = this.state;
		if ((this.state.level.number % 3) === 0) {
			lives++;
			console.log(`Levelled ${this.state.level.number} => ${newLevelNumber}. Bonus life awarded ${this.state.lives} => ${lives}`)
		}

		console.log(`onBonusCountdownComplete: Starting level ${newLevelNumber}`);

		const level = createLevel(newLevelNumber)
		this.setState({
			...FrenzyMain.getLevelStartState(),
			lives,
			level,
			currentBonusDisplayed: level.bonusMultiplier,
		});
		this.lastUpdatedCounter = new Date().getTime();
	}

	requestCounterPauseState = (counterEnabled: boolean) => {
		this.setState({
			counterEnabled
		})
	}

	decrementCounter = () => {
		const { counterValue, counterEnabled, performBonusCountdown, counterValueAtBonusCountdown} = this.state
		if (performBonusCountdown) {
			if (counterValue <= 0) {
				this.onBonusCountdownComplete();
				return;
			}

			// counterValueAtBonusCountdown * this.state.level.bonusMultiplier

			// const score = this.state.score + bonusPointsScored;


			// Decrement as fast as we can
			this.setState(oldState => ({
				counterValue: minMax(0, oldState.counterValue - 10)[1],
				score: oldState.score + (10 * oldState.level.bonusMultiplier)
			}))
			;
			return;
		}

		if (counterEnabled) {
			const now = new Date().getTime();
			const delta = (now - this.lastUpdatedCounter);
	
			if (delta > 1000) {
				this.setState(oldState => ({
					counterValue: minMax(0, oldState.counterValue - 10)[1]
				}), () => {
					this.lastUpdatedCounter = now;
				});
			}
		}
	}

  update = () => {
		if (this.state.pauseRequested || this.state.paused) {
			this.handleKeysWhilePaused();
			return; // the game is paused, do not decrement the counter
		}
		this.handleGameplayKeys();
		this.decrementCounter();
	};

	renderPausedScreen = () => {
		return (
			<React.Fragment>
				<br />
				<br />
				<h1 className="Yellow">PAUSED</h1>
				<br/>
				<h3>Press Q to quit</h3>
				<br />
				<h4>or</h4>
				<br />
				<h3>Press P to resume</h3>
			</React.Fragment>
		);
	}
	
	renderGameplayScreen = (paused: boolean) => {
		return (
			<Stage width={this.state.scale * SCREEN_WIDTH} height={this.state.scale * SCREEN_HEIGHT} style={{ visibility: (paused ? 'hidden' : 'visible') }}>
				<Scoreboard 
					score={this.state.score}
					lives={this.state.lives}
					drawMode={this.state.drawMode} 
					bonusMultiplier={this.state.currentBonusDisplayed}
					counterValue={this.state.counterValue}
					scale={this.state.scale}
				/>
				
				<GameArea
					key={this.state.level.number}
					scale={this.state.scale}
					paused={paused}
					drawMode={this.state.drawMode}
					level={this.state.level}
					onScoreIncrement={this.onScoreIncrement}
					onLevelTransitionFinished={this.onLevelTransitionFinished}
					onFinalScoreCalculated={this.onFinalScoreCalculated}
					onDrawFinished={this.onDrawFinished}
					onPlayerDied={this.onPlayerDied}
					onLeptonsKilled={this.onLeptonsKilled}
					onFinalLeptonKilled={this.onFinalLeptonKilled}
					onCompletionPercentageChanged={this.onCompletionPercentageChanged}
					requestCounterPauseState={this.requestCounterPauseState}
				/>
				
				<CompletionMeter completionPercentage={this.state.completionPercentage} />
			</Stage>
		);
	}

  render() {
		// Important: Always render the GameArea, otherwise it'll get disposed-of
		// and there'll be nothing to unpause to...
		return (
			<React.Fragment>
				{
					this.state.paused && this.renderPausedScreen()
				}
				{
					this.renderGameplayScreen(this.state.paused)
				}
			</React.Fragment>
		);
  }
}


export default FrenzyMain;
